import { AddIcon } from "@minket/brand/icons/add";
import { TemplatesIcon } from "@minket/brand/icons/templates";
import { Button } from "@minket/ui/components/button";
import { EmptyState } from "@minket/ui/components/empty-state";
import { StatusBadge } from "@minket/ui/components/status-badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@minket/ui/components/table";
import { cn } from "@minket/ui/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Fragment, lazy, Suspense, useState } from "react";
import { UnavailableAction } from "@/components/page/action";
import { FadeText } from "@/components/page/fade-text";
import { GhostTable } from "@/components/page/ghost";
import { Knot, type KnotKind } from "@/components/page/knot";
import { Page } from "@/components/page/page";
import { Scoped } from "@/components/page/scope";
import { QueryView } from "@/components/page/states";
import {
  formatTime,
  type TemplateListItem,
  templateStatus,
  versionLabel,
} from "@/components/templates/format";
import { endpoints } from "@/lib/endpoints";
import { useCan } from "@/lib/permissions";
import { apiQuery } from "@/lib/query";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/templates/")({
  component: Templates,
});

const CreateTemplate = lazy(() =>
  import("@/components/templates/create-template.ondemand").then((m) => ({
    default: m.CreateTemplateDialog,
  })),
);

const columns = ["Status", "Template", "Versions", "Updated, UTC"] as const;
const PAGE = 50;
/** The latest versions a row draws; earlier ones are a faint lead-in. */
const KNOTS = 6;

function Templates() {
  const { project } = useWorkspace();
  const [after, setAfter] = useState<string | undefined>();
  const [creating, setCreating] = useState(false);
  const list = useQuery({
    ...apiQuery(endpoints.templates, { projectId: project?.id ?? "" }, { limit: PAGE, after }),
    enabled: project !== null,
  });
  const canManage = useCan("templates.manage");
  const action =
    project && canManage ? (
      <Button onClick={() => setCreating(true)}>
        <AddIcon size={20} />
        New template
      </Button>
    ) : (
      <UnavailableAction
        label="New template"
        icon={AddIcon}
        reason={project ? "Viewers can read templates but not create them" : "Needs a project"}
      />
    );

  return (
    <Page title="Templates" action={action}>
      <Scoped
        scope="project"
        will="Each template will be listed here with its status and its versions."
        icon={<TemplatesIcon size={20} />}
        preview={(busy) => <GhostTable columns={columns} label="templates" busy={busy} />}
      >
        {() => (
          <QueryView
            query={list}
            noun="templates"
            preview={(busy) => <GhostTable columns={columns} label="templates" busy={busy} />}
          >
            {(page) =>
              page.data.length === 0 && after === undefined ? (
                <EmptyState
                  data-state="empty"
                  icon={<TemplatesIcon size={20} />}
                  title="No templates yet"
                  description="Build one from blocks here, or push React Email output from the CLI."
                  preview={<GhostTable columns={columns} label="templates" />}
                />
              ) : (
                <TemplateTable
                  rows={page.data}
                  hasMore={page.has_more}
                  paged={after !== undefined}
                  onNext={() => setAfter(page.data.at(-1)?.id)}
                  onFirst={() => setAfter(undefined)}
                />
              )
            }
          </QueryView>
        )}
      </Scoped>
      {creating && project ? (
        <Suspense fallback={null}>
          <CreateTemplate projectId={project.id} onClose={() => setCreating(false)} />
        </Suspense>
      ) : null}
    </Page>
  );
}

/** A version's knot: plate ink for the published one, dashed for a newer draft, faint otherwise. */
function knotOf(version: number, template: TemplateListItem): KnotKind {
  if (version === template.current_version && version !== template.published_version) {
    return "waiting";
  }
  return "held";
}
const superseded = (version: number, template: TemplateListItem) =>
  version !== template.published_version && version !== template.current_version;

/** The template's versions as a short chain, drawn beside the words that say the same. */
function VersionKnots({ template }: { template: TemplateListItem }) {
  const first = Math.max(1, template.current_version - KNOTS + 1);
  const shown = Array.from(
    { length: template.current_version - first + 1 },
    (_, index) => first + index,
  );
  return (
    <span aria-hidden className="inline-flex items-center">
      {first > 1 ? <span className="w-3 border-t-[1.5px] border-dashed border-seal-faint" /> : null}
      {shown.map((version, index) => (
        <Fragment key={version}>
          {index > 0 ? (
            <span
              className={cn(
                "w-2.5 border-t-[1.5px] border-dotted",
                superseded(version, template) ? "border-seal-faint" : "border-thread",
              )}
            />
          ) : null}
          <Knot
            kind={knotOf(version, template)}
            className={superseded(version, template) ? "text-seal-faint" : undefined}
          />
        </Fragment>
      ))}
    </span>
  );
}

function TemplateTable({
  rows,
  hasMore,
  paged,
  onNext,
  onFirst,
}: {
  rows: readonly TemplateListItem[];
  hasMore: boolean;
  paged: boolean;
  onNext: () => void;
  onFirst: () => void;
}) {
  return (
    <div className="grid min-w-0 gap-3">
      <Table aria-label="Hosted templates, newest first">
        <TableHeader>
          <TableRow>
            <TableHead className="hidden sm:table-cell">Status</TableHead>
            <TableHead>Template</TableHead>
            <TableHead className="hidden sm:table-cell">Versions</TableHead>
            <TableHead className="hidden text-right sm:table-cell">Updated, UTC</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((template) => {
            const status = templateStatus(template);
            return (
              <TableRow key={template.id}>
                <TableCell className="hidden whitespace-nowrap sm:table-cell">
                  <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
                </TableCell>
                <TableCell className="w-full max-w-0">
                  <Link
                    to="/templates/$id"
                    params={{ id: template.id }}
                    className="block w-fit max-w-full rounded-sm font-medium decoration-1 underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                  >
                    <FadeText>{template.name}</FadeText>
                  </Link>
                  <span className="mt-0.5 flex min-w-0 items-baseline gap-3 text-[12.5px] text-muted-foreground">
                    {template.alias ? (
                      <FadeText className="type-data text-[12.5px]">{template.alias}</FadeText>
                    ) : null}
                    <span className="shrink-0 whitespace-nowrap">
                      {template.engine === "html" ? "HTML" : "Blocks"}
                    </span>
                  </span>
                  <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12.5px] text-muted-foreground sm:hidden">
                    <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
                    <span className="type-data whitespace-nowrap">{versionLabel(template)}</span>
                    <time dateTime={template.updated_at} className="type-data whitespace-nowrap">
                      {formatTime(template.updated_at)}
                    </time>
                  </span>
                </TableCell>
                <TableCell className="hidden whitespace-nowrap sm:table-cell">
                  <span className="grid justify-items-start gap-1">
                    <VersionKnots template={template} />
                    <span className="type-data text-[12.5px] text-muted-foreground">
                      {versionLabel(template)}
                    </span>
                  </span>
                </TableCell>
                <TableCell className="hidden text-right sm:table-cell">
                  <time
                    dateTime={template.updated_at}
                    className="type-data text-[13px] whitespace-nowrap"
                  >
                    {formatTime(template.updated_at)}
                  </time>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {hasMore || paged ? (
        <nav aria-label="Pages of templates" className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" size="sm" disabled={!paged} onClick={onFirst}>
            Newest
          </Button>
          <Button variant="outline" size="sm" disabled={!hasMore} onClick={onNext}>
            Older
          </Button>
        </nav>
      ) : null}
    </div>
  );
}
